import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { X, ChevronLeft, ChevronRight, Play, Image, Film, Building2, Sparkles } from 'lucide-react'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import Reveal from '../components/landing/Reveal'

const albums = [
  {
    id: 'ai-generated',
    title: 'AI-Generated Work',
    icon: Sparkles,
    description: 'Student projects created using AI tools — see the prompts and results.',
    cover: '/Gallery/ai-generated/Logo Design for Local Business.png',
    category: 'AI-Generated',
    items: [
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/Logo Design for Local Business.png', 
        prompt: 'Modern minimalist logo for Kampala coffee shop, incorporating traditional Ugandan patterns, warm earth tones, clean typography',
        caption: 'Logo Design for Local Business',
        tool: 'Midjourney + Adobe Illustrator'
      },
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/AI-Enhanced Product Shot.png', 
        prompt: 'Professional product photography, studio lighting, white background, commercial quality for e-commerce',
        caption: 'AI-Enhanced Product Shot',
        tool: 'Adobe Firefly + Photoshop'
      },
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/AI Color Grading.png', 
        prompt: 'Cinematic color grade, teal and orange color palette, film photography aesthetic, moody atmosphere',
        caption: 'AI Color Grading',
        tool: 'Adobe Sensei'
      },
    ],
  },
  {
    id: 'studio',
    title: 'Studio & Campus',
    icon: Building2,
    description: 'Our professional studio spaces, equipment, and campus facilities.',
    cover: '/Gallery/Academy/124414.jpg',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/Academy/124414.jpg', caption: 'SMA Studio' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02406 copy.jpg', caption: 'Studio Setup' },
      { type: 'image', src: '/Gallery/DSC02420 copy.webp', caption: 'Studio Space' },
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg', caption: 'Equipment Room' },
      { type: 'image', src: '/Gallery/Academy/Multimedia Workshops/DSC02424 copy.jpg', caption: 'Production Area' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02428 copy.jpg', caption: 'Workspace' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02432 copy.jpg', caption: 'Creative Space' },
      { type: 'image', src: '/Gallery/Academy/Multimedia Workshops/DSC02588.jpg', caption: 'Campus View' },
    ],
  },
  {
    id: 'photography',
    title: 'Photography Sessions',
    icon: Image,
    description: 'Behind the scenes from our photography classes and student shoots.',
    cover: '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/268A5754.JPG', caption: 'Photography Class' },
      { type: 'image', src: '/Gallery/Academy/Future Storytellers/Kelly  (1).jpeg', caption: 'Lighting Techniques' },
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/268A5756.JPG', caption: 'Portrait Session' },
      { type: 'image', src: '/Gallery/268A5758.JPG', caption: 'Student Work' },
    ],
  },
  {
    id: 'classrooms',
    title: 'Classrooms & Training',
    icon: Building2,
    description: 'Our modern classrooms and hands-on training sessions in action.',
    cover: '/Gallery/Academy/Class rooms/20250822_163612.jpg',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_163612.jpg', caption: 'Classroom Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_164614.jpg', caption: 'Theory Class' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_164638.jpg', caption: 'Group Work' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_164713.jpg', caption: 'Practical Lesson' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_165220.jpg', caption: 'Student Presentation' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165442.jpg', caption: 'Lab Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165604.jpg', caption: 'Training in Progress' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165622.jpg', caption: 'Instructor Demo' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165729.jpg', caption: 'Hands-on Training' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_171029.jpg', caption: 'Creative Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_171103.jpg', caption: 'Class in Action' },
    ],
  },
  {
    id: 'videos',
    title: 'Video Showcase',
    icon: Film,
    description: 'Video tours, lesson previews, and student productions.',
    cover: 'https://img.youtube.com/vi/nIkutyG6vu4/hqdefault.jpg',
    category: 'Video',
    items: [
      { type: 'video', youtubeId: 'nIkutyG6vu4', caption: 'Synergy Multimedia Academy Tour | Reception & Professional Studio Overview' },
      { type: 'video', youtubeId: '35lUT3wQ_cE', caption: 'Camera Lessons in Uganda | Photography & Videography Training for Beginners' },
      { type: 'video', youtubeId: 'D_Td2RFYxiM', caption: 'Photoshop Classes in Uganda | Graphic Design Training at Synergy Multimedia Academy' },
    ],
  },
]

const HERO_IMAGE = '/Gallery/Academy/Class rooms/20250822_165220.jpg'
const CONTACT_EMAIL = 'synergymultimediaacademy@gmail.com'

const isVideo = (item) => item.type === 'video'
const countLabel = (album) => {
  const n = album.items.length
  return album.items.every(isVideo) ? `${n} video${n === 1 ? '' : 's'}` : `${n} photo${n === 1 ? '' : 's'}`
}

// Equal-height frames on a 12-column grid: 2 per row on phones, 3 on tablets, 4 on desktop.
// Photos in an incomplete last row widen to fill it, so no row has a gap.
function spanClasses(index, total) {
  const lastRowSize = (cols) => {
    const rest = total % cols
    return rest && index >= total - rest ? rest : 0
  }
  const sm = lastRowSize(2)
  const md = lastRowSize(3)
  const lg = lastRowSize(4)
  return [
    sm === 1 ? 'col-span-12' : 'col-span-6',
    md === 1 ? 'md:col-span-12' : md === 2 ? 'md:col-span-6' : 'md:col-span-4',
    lg === 1 ? 'lg:col-span-12' : lg === 2 ? 'lg:col-span-6' : lg === 3 ? 'lg:col-span-4' : 'lg:col-span-3',
  ].join(' ')
}

function PhotoGrid({ album, onOpen }) {
  const total = album.items.length
  return (
    <div className="grid grid-cols-12 gap-3 sm:gap-4">
      {album.items.map((item, index) => (
        <button
          key={`${item.src}-${index}`}
          type="button"
          onClick={() => onOpen(index)}
          className={`group relative h-40 sm:h-52 lg:h-60 overflow-hidden bg-white/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cinema-gold ${spanClasses(index, total)}`}
          aria-label={`Open ${item.caption}`}
        >
          <img
            src={item.src}
            alt={item.caption}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pb-3 pt-12 text-left opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100">
            <span className="block text-sm text-white font-sans leading-snug">{item.caption}</span>
            {item.tool && (
              <span className="mt-1 block text-[0.65rem] uppercase tracking-[0.16em] text-cinema-gold font-sans">{item.tool}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  )
}

function VideoGrid({ album, onOpen }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {album.items.map((item, index) => (
        <button
          key={item.youtubeId || item.src || index}
          type="button"
          onClick={() => onOpen(index)}
          className="group text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cinema-gold"
        >
          <span className="relative block aspect-video overflow-hidden bg-black">
            {item.youtubeId && (
              <img
                src={`https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover opacity-75 transition duration-500 group-hover:opacity-100 group-hover:scale-[1.03]"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cinema-glow text-white shadow-lg shadow-black/50 transition-transform duration-300 group-hover:scale-110">
                <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" aria-hidden="true" />
              </span>
            </span>
          </span>
          <span className="mt-3 block text-sm sm:text-base text-white font-sans leading-snug transition-colors group-hover:text-cinema-gold">
            {item.caption}
          </span>
        </button>
      ))}
    </div>
  )
}

export default function GalleryPage() {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [filter, setFilter] = useState('all')
  const [lightbox, setLightbox] = useState(null) // { albumId, index }

  const visibleAlbums = filter === 'all' ? albums : albums.filter((album) => album.id === filter)
  const photoTotal = albums.reduce((n, a) => n + a.items.filter((i) => !isVideo(i)).length, 0)
  const videoTotal = albums.reduce((n, a) => n + a.items.filter(isVideo).length, 0)

  const lbAlbum = lightbox ? albums.find((a) => a.id === lightbox.albumId) : null
  const lbItem = lbAlbum ? lbAlbum.items[lightbox.index] : null

  const step = (delta) =>
    setLightbox((current) => {
      if (!current) return current
      const album = albums.find((a) => a.id === current.albumId)
      const total = album.items.length
      return { ...current, index: (current.index + delta + total) % total }
    })

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!lightbox) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null)
      if (e.key === 'ArrowRight') step(1)
      if (e.key === 'ArrowLeft') step(-1)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [lightbox])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Gallery | Studios, Classes & Student Work | Synergy Multimedia Academy Kampala</title>
        <meta
          name="description"
          content="Photos and videos from Synergy Multimedia Academy in Kampala, Uganda: our studios and classrooms, photography sessions, student work and AI creative projects."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/gallery" />
      </Helmet>
      <div className="cinema-grain" aria-hidden="true" />

      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={(id) => navigate(`/#${id}`)} />

      <header className="relative isolate flex min-h-[56svh] items-end overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 -z-10" aria-hidden="true">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover grayscale object-[center_55%]" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
        </div>
        <div className="w-full max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 pt-40 pb-16 sm:pt-48 sm:pb-20 lg:pt-56 lg:pb-24">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Life at SMA</p>
            <h1 className="mt-6 font-display text-4xl sm:text-6xl text-white font-light tracking-tight leading-[1.08]">
              Our studios, classes and student work.
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-white/80 font-sans leading-relaxed">
              {photoTotal} photos and {videoTotal} videos from our academy in Kampala.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <nav aria-label="Gallery albums" className="pt-12 sm:pt-16">
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:px-0 [scrollbar-width:none]">
            {[{ id: 'all', title: 'All' }, ...albums].map((album) => {
              const active = filter === album.id
              return (
                <button
                  key={album.id}
                  type="button"
                  onClick={() => setFilter(album.id)}
                  aria-pressed={active}
                  className={`shrink-0 border px-4 py-2 text-xs uppercase tracking-[0.14em] font-sans font-semibold transition-colors ${
                    active
                      ? 'border-cinema-gold/60 bg-cinema-gold/10 text-cinema-gold'
                      : 'border-white/10 text-cinema-soft hover:border-white/30 hover:text-white'
                  }`}
                >
                  {album.title}
                </button>
              )
            })}
          </div>
        </nav>

        {visibleAlbums.map((album) => (
          <section
            key={album.id}
            id={`album-${album.id}`}
            aria-labelledby={`album-${album.id}-title`}
            className="py-14 sm:py-20 border-b border-white/10 last:border-b-0"
          >
            <div className="mb-8 sm:mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold">{countLabel(album)}</p>
                <h2 id={`album-${album.id}-title`} className="mt-3 font-display text-3xl sm:text-4xl text-white font-light tracking-tight">
                  {album.title}
                </h2>
              </div>
              <p className="max-w-md text-sm text-cinema-muted font-sans leading-relaxed sm:text-right">{album.description}</p>
            </div>
            {album.items.every(isVideo) ? (
              <VideoGrid album={album} onOpen={(index) => setLightbox({ albumId: album.id, index })} />
            ) : (
              <PhotoGrid album={album} onOpen={(index) => setLightbox({ albumId: album.id, index })} />
            )}
          </section>
        ))}

        <section className="py-20 sm:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white font-light tracking-tight">Come and see it in person.</h2>
          <p className="mt-3 text-base text-cinema-soft/85 font-sans">Visit the studio, meet a trainer, or start your application.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/intake" className="cinema-btn-primary">Apply now</Link>
            <Link to="/advisor" className="cinema-btn-ghost">Talk to an Advisor</Link>
          </div>
        </section>
      </main>

      <CinematicFooter contactEmail={CONTACT_EMAIL} closing={false} />

      {lbItem && (
        <div
          className="fixed inset-0 z-[70] flex flex-col bg-black"
          role="dialog"
          aria-modal="true"
          aria-label={lbItem.caption}
          onClick={() => setLightbox(null)}
        >
          <div className="flex h-16 shrink-0 items-center justify-between px-4 sm:px-6 text-sm text-white/70 font-sans">
            <span>
              {lbAlbum.title} · {lightbox.index + 1} / {lbAlbum.items.length}
            </span>
            <button type="button" onClick={() => setLightbox(null)} className="p-2 hover:text-white" aria-label="Close">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-12 sm:px-20 pb-6">
            {lbAlbum.items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); step(-1) }}
                  className="absolute left-1 sm:left-4 p-2 text-white/60 hover:text-white"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-9 w-9" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); step(1) }}
                  className="absolute right-1 sm:right-4 p-2 text-white/60 hover:text-white"
                  aria-label="Next"
                >
                  <ChevronRight className="h-9 w-9" />
                </button>
              </>
            )}
            <figure className="flex w-full max-w-6xl flex-col items-center" onClick={(e) => e.stopPropagation()}>
              {isVideo(lbItem) ? (
                lbItem.youtubeId ? (
                  <iframe
                    key={lbItem.youtubeId}
                    className="aspect-video w-full max-h-[75vh] bg-black"
                    src={`https://www.youtube.com/embed/${lbItem.youtubeId}?autoplay=1&rel=0`}
                    title={lbItem.caption}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video key={lbItem.src} className="max-h-[75vh] w-full bg-black" controls autoPlay>
                    <source src={lbItem.src} type="video/mp4" />
                  </video>
                )
              ) : (
                <img src={lbItem.src} alt={lbItem.caption} className="max-h-[75vh] max-w-full object-contain" />
              )}
              <figcaption className="mt-4 max-w-2xl text-center font-sans">
                <span className="block text-sm sm:text-base text-white/90">{lbItem.caption}</span>
                {lbItem.prompt && <span className="mt-2 block text-sm italic text-white/60">“{lbItem.prompt}”</span>}
                {lbItem.tool && (
                  <span className="mt-2 block text-[0.65rem] uppercase tracking-[0.18em] text-cinema-gold">{lbItem.tool}</span>
                )}
              </figcaption>
            </figure>
          </div>
        </div>
      )}
    </div>
  )
}
